import { useSyncExternalStore } from "react";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => void;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const isIOS = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

export type PwaPlatform = "ios" | "android" | "desktop";

export const getPwaPlatform = (): PwaPlatform => {
  if (isIOS()) return "ios";
  if (/Android/i.test(navigator.userAgent)) return "android";
  return "desktop";
};

const INSTALLED_KEY = "pwaInstalled";
const IOS_DISMISSED_KEY = "iosPwaDismissed";

const isMarkedInstalled = () => localStorage.getItem(INSTALLED_KEY) === "true";

const isInStandaloneMode = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  (window.navigator as any).standalone === true;

/*
 * Install state lives at module level rather than per hook call: the browser
 * fires beforeinstallprompt once per page load, often before the components
 * showing the banner have mounted, and every caller must see the same
 * banner state (dismissing it in one place hides it everywhere).
 */
interface PwaInstallState {
  installPrompt: BeforeInstallPromptEvent | null;
  showBanner: boolean;
  showIOSBanner: boolean;
}

let state: PwaInstallState = {
  installPrompt: null,
  showBanner: false,
  showIOSBanner: false,
};

const listeners = new Set<() => void>();

const setState = (patch: Partial<PwaInstallState>) => {
  state = { ...state, ...patch };
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getSnapshot = () => state;

const init = () => {
  if (isMarkedInstalled() || isInStandaloneMode()) return;

  // iOS never fires beforeinstallprompt, so its banner is known up front
  if (isIOS()) {
    if (!sessionStorage.getItem(IOS_DISMISSED_KEY)) {
      state = { ...state, showBanner: true, showIOSBanner: true };
    }
    return;
  }

  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    setState({
      installPrompt: e as BeforeInstallPromptEvent,
      showBanner: true,
    });
  });

  window.addEventListener("appinstalled", () => {
    localStorage.setItem(INSTALLED_KEY, "true");
    setState({ installPrompt: null, showBanner: false });
  });
};

if (typeof window !== "undefined") init();

const promptInstall = async () => {
  const { installPrompt } = state;
  if (!installPrompt) return;

  // The event can only be prompted once — clear it before awaiting so a
  // second click can't reuse it
  setState({ installPrompt: null });
  installPrompt.prompt();
  const { outcome } = await installPrompt.userChoice;
  if (outcome === "accepted") {
    localStorage.setItem(INSTALLED_KEY, "true");
  }
  setState({ showBanner: false });
};

const dismissBanner = () => {
  setState({ installPrompt: null, showBanner: false, showIOSBanner: false });
  if (isIOS()) {
    sessionStorage.setItem(IOS_DISMISSED_KEY, "true");
  }
};

export const usePWAInstall = () => {
  const { showBanner, showIOSBanner } = useSyncExternalStore(
    subscribe,
    getSnapshot
  );
  return { showBanner, showIOSBanner, promptInstall, dismissBanner };
};
